import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import moment from 'moment';
import { FaChartLine, FaArrowsAltH, FaRegClock } from 'react-icons/fa';
import { getAttendanceBorderColor } from '../../../utils/calendarUtils';
import './AttendanceGraph.css';

const AttendanceGraph = ({ dailyReport, month, year }) => {
  // Transform data for the graph - Keep all days
  const chartData = useMemo(() => {
    if (!dailyReport || dailyReport.length === 0) return [];
    return dailyReport.map(day => {
      const dayNum = parseInt(day.date, 10); 
      let inMinutes = 615; // Adjusted to remove "Middle Gap" (10:15 AM baseline / Off)
      let hasMorningIn = false;

      const topStatuses = ['Present', 'Late', 'EarlyGoing', 'Half-day', 'Miss-Punch'];

      if (topStatuses.includes(day.status)) {
        if (day.in && day.in !== '-' && day.in !== '') {
          const time = moment(day.in, ["HH:mm", "h:mm A", "H:mm"]);
          if (time.isValid()) {
            const mins = time.hours() * 60 + time.minutes();
            if (mins < 840) {
              inMinutes = mins;
              hasMorningIn = true;
            } else if (day.status === 'Miss-Punch') {
              inMinutes = 570; 
            }
          }
        } else if (day.status === 'Miss-Punch') {
          inMinutes = 570;
        }
      }

      return {
        day: !isNaN(dayNum) ? (dayNum < 10 ? `0${dayNum}` : `${dayNum}`) : day.date,
        dayNum: !isNaN(dayNum) ? dayNum : 0,
        inTime: inMinutes,
        status: day.status || 'Absent',
        in: day.in,
        out: day.out,
        hasMorningIn
      };
    });
  }, [dailyReport]);

  // Quick summary counts for header badges
  const stats = useMemo(() => {
    let onTime = 0;
    let late = 0;
    let missPunch = 0;
    let absent = 0;
    let off = 0;

    if (!dailyReport || dailyReport.length === 0) {
      return { onTime, late, missPunch, absent, off };
    }

    dailyReport.forEach(day => {
      const st = (day.status || '').toLowerCase();
      if (st === 'late') {
        late++;
      } else if (st === 'present') {
        if (day.in && day.in !== '-' && day.in !== '') {
          const time = moment(day.in, ["HH:mm", "h:mm A", "H:mm"]);
          if (time.isValid() && (time.hours() * 60 + time.minutes()) > 570) {
            late++;
            return;
          }
        }
        onTime++;
      } else if (st.includes('miss-punch') || st.includes('misspunch')) {
        missPunch++;
      } else if (st === 'absent') {
        absent++;
      } else if (st.includes('off') || st.includes('holiday')) {
        off++;
      }
    });

    return { onTime, late, missPunch, absent, off };
  }, [dailyReport]);

  // Early return after all hooks have been declared
  if (!dailyReport || dailyReport.length === 0 || chartData.length === 0) {
    return null;
  }

  // High-precision ticks optimized for height
  const yTicks = [540, 555, 570, 575, 585, 600, 615];

  // Format Y-axis labels
  const formatYAxis = (tickItem) => {
    if (tickItem === 615) return 'Off';
    const hours = Math.floor(tickItem / 60);
    const minutes = tickItem % 60;
    return `${hours}:${minutes < 10 ? '0' + minutes : minutes}am`;
  };

  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    if (!cx || !cy) return null;
    const color = getAttendanceBorderColor(payload.status);
    return (
      <circle 
        cx={cx} 
        cy={cy} 
        r={props.active ? 6.5 : 4.5} 
        fill={color} 
        stroke="#ffffff" 
        strokeWidth={1.5} 
      />
    );
  };

  const yDomain = [540, 615];

  return (
    <div id="attendance-ratio-container" className="attendance-graph-container p-3 p-md-4">
      {/* Header with Title and Quick Stats */}
      <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-2 mb-3">
        <div>
          <h5 className="attendance-graph-title d-flex align-items-center mb-1">
            <FaChartLine className="text-primary me-2 flex-shrink-0" />
            <span>Monthly Punch-In Time Ratio Analysis</span>
          </h5>
          <div className="attendance-graph-subtitle d-flex align-items-center gap-2 flex-wrap">
            <span>Target: <strong className="text-success">9:30 AM</strong></span>
            <span>•</span>
            <span>Grace Period: <strong className="text-warning">9:35 AM</strong></span>
          </div>
        </div>

        {/* Quick summary badges */}
        <div className="d-flex flex-wrap gap-2 mt-1 mt-lg-0">
          <span className="attendance-stat-pill pill-on-time" title="Punch-in at or before 9:30 AM">
            <span className="attendance-legend-dot" style={{ backgroundColor: '#28a745' }}></span>
            On-Time: {stats.onTime}
          </span>
          <span className="attendance-stat-pill pill-late" title="Punch-in after 9:30 AM">
            <span className="attendance-legend-dot" style={{ backgroundColor: '#fbc02d' }}></span>
            Late: {stats.late}
          </span>
          {stats.missPunch > 0 && (
            <span className="attendance-stat-pill pill-miss-punch" title="Missed punch recordings">
              <span className="attendance-legend-dot" style={{ backgroundColor: '#0d6efd' }}></span>
              Miss-Punch: {stats.missPunch}
            </span>
          )}
          {stats.absent > 0 && (
            <span className="attendance-stat-pill pill-absent" title="Absent days">
              <span className="attendance-legend-dot" style={{ backgroundColor: '#dc3545' }}></span>
              Absent: {stats.absent}
            </span>
          )}
        </div>
      </div>

      {/* Mobile Swipe Hint */}
      <div className="attendance-graph-mobile-hint align-items-center justify-content-between mb-2">
        <span className="badge bg-light text-secondary border px-2 py-1 fw-normal d-inline-flex align-items-center gap-1">
          <FaArrowsAltH /> Swipe horizontally to view full month
        </span>
        <span className="text-muted small d-inline-flex align-items-center">
          <FaRegClock className="me-1 text-primary" /> {chartData.length} Days
        </span>
      </div>

      {/* Horizontally scrollable container on mobile */}
      <div className="attendance-graph-scroll-wrapper">
        <div className="attendance-graph-inner">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 90, left: 10, bottom: 25 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={true} horizontal={true} stroke="#f1f5f9" />
              
              <XAxis 
                dataKey="day" 
                label={{ 
                  value: 'Day of Month', 
                  position: 'bottom', 
                  offset: 5, 
                  fill: '#64748b', 
                  fontSize: 11, 
                  fontWeight: 600 
                }}
                tick={{ fontSize: 10.5, fill: '#64748b' }}
                interval={0}
                tickLine={{ stroke: '#cbd5e1' }}
                axisLine={{ stroke: '#cbd5e1' }}
              />

              <YAxis 
                domain={yDomain}
                ticks={yTicks}
                width={65}
                tickFormatter={formatYAxis}
                label={{ 
                  value: 'In Time', 
                  angle: -90, 
                  position: 'insideLeft', 
                  offset: 12, 
                  fill: '#64748b', 
                  fontSize: 11, 
                  fontWeight: 600 
                }}
                tick={{ fontSize: 10, fill: '#64748b' }}
                interval={0}
                reversed={true}
                tickLine={{ stroke: '#cbd5e1' }}
                axisLine={{ stroke: '#cbd5e1' }}
              />

              <Tooltip 
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    const statusColor = getAttendanceBorderColor(data.status);
                    const isLate = data.status === 'Late' || (data.hasMorningIn && data.inTime > 570);
                    return (
                      <div className="attendance-tooltip-card bg-white p-3 border rounded shadow-lg" style={{ minWidth: 165 }}>
                        <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2 gap-2">
                          <strong className="text-dark">Day {label}</strong>
                          <span 
                            className="badge px-2 py-1 rounded-pill"
                            style={{ backgroundColor: statusColor, color: '#ffffff', fontSize: '10.5px' }}
                          >
                            {data.status}
                          </span>
                        </div>
                        <div className="small">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="text-muted">In Time:</span>
                            <span className={`fw-bold ${isLate ? 'text-warning' : 'text-dark'}`}>{data.in || '-'}</span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Out Time:</span>
                            <span className="fw-bold text-dark">{data.out || '-'}</span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />

              {/* 9:30 AM Green Line */}
              <ReferenceLine 
                y={570} 
                stroke="#10b981" 
                strokeWidth={2} 
                strokeDasharray="4 4"
                label={{ 
                  value: '9:30 AM (Target)', 
                  position: 'right', 
                  fill: '#10b981', 
                  fontSize: 10.5, 
                  fontWeight: 'bold' 
                }} 
              />

              {/* 9:35 AM Yellow Line */}
              <ReferenceLine 
                y={575} 
                stroke="#f59e0b" 
                strokeWidth={2} 
                strokeDasharray="4 4"
                label={{ 
                  value: '9:35 AM (Late)', 
                  position: 'right', 
                  fill: '#d97706', 
                  fontSize: 10.5, 
                  fontWeight: 'bold' 
                }} 
              />

              <Line 
                type="monotone" 
                dataKey="inTime" 
                stroke="#94a3b8" 
                strokeWidth={2.2}
                connectNulls={true}
                dot={<CustomDot />}
                activeDot={{ r: 7, stroke: '#ffffff', strokeWidth: 2 }}
                animationDuration={1200}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Legend Matching Image Statuses + Reference Lines */}
      <div className="attendance-graph-legend mt-3">
        {/* Reference Line items */}
        <div className="attendance-legend-chip">
          <span className="attendance-legend-line-ref" style={{ backgroundColor: '#10b981' }}></span>
          Target (9:30 AM)
        </div>
        <div className="attendance-legend-chip">
          <span className="attendance-legend-line-ref" style={{ backgroundColor: '#f59e0b' }}></span>
          Late Grace (9:35 AM)
        </div>

        {/* Status items */}
        {[
          { label: 'Present', color: '#28a745' },
          { label: 'Late', color: '#fbc02d' },
          { label: 'Early-Going', color: '#c217e8' },
          { label: 'Miss-Punch', color: '#0d6efd' },
          { label: 'Absent', color: '#dc3545' },
          { label: 'W. Off', color: '#6c757d' }
        ].map(item => (
          <div key={item.label} className="attendance-legend-chip">
            <span className="attendance-legend-dot" style={{ backgroundColor: item.color }}></span>
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AttendanceGraph;
